import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RecaptchaVerifier as FirebaseRecaptchaVerifier, type ConfirmationResult, type User } from "firebase/auth";
import { auth } from "../firebaseConfig";
import { CssVarsProvider } from "@mui/joy/styles";
import GlobalStyles from "@mui/joy/GlobalStyles";
import CssBaseline from "@mui/joy/CssBaseline";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Checkbox from "@mui/joy/Checkbox";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Link from "@mui/joy/Link";
import Typography from "@mui/joy/Typography";
import GoogleIcon from "../GoogleIcon";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Loading from "../components/Loading";
import RightSide from "../components/RightSide";
import { ErrorDisplay } from "../components/ErrorDisplay";
import { OTPVerification } from "../components/OTPVerification";
import { PhoneNumberInput } from "../components/PhoneNumberInput";
import { PasswordStrengthInput } from "../components/PasswordStrengthInput";
import { customTheme } from "../config/theme";
import { authService } from "../services/authService";
import { sanitizeUserVisibleMessage } from "../utils/errorMessages";
import { validators } from "../utils/validators";
import { getSafeRedirectUrl } from "../utils/redirectValidator";

type SignupMethod = "initial" | "email" | "phone" | "google";

interface SignupState {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  loading: boolean;
  error: string | null;
  authLoading: boolean;
  signupMethod: SignupMethod;
}

const initialState: SignupState = {
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
  phone: "",
  loading: false,
  error: null,
  authLoading: true,
  signupMethod: "initial",
};

export default function Signup() {
  const [state, setState] = useState<SignupState>(initialState);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [pendingUser, setPendingUser] = useState<User | null>(null);
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const recaptchaRef = useRef<FirebaseRecaptchaVerifier | null>(null);

  const [searchParams] = useSearchParams();
  const redirectUrl = getSafeRedirectUrl(searchParams);

  useEffect(() => {
    document.title = "Sign up | Iskcon Kanpur";
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_API_URL}/api/user/authcheck`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (response.ok) {
          window.location.href = redirectUrl;
        }
      } catch (error) {
        console.error("Auth check failed:", error);
      } finally {
        setState((s) => ({ ...s, authLoading: false }));
      }
    };

    checkAuth();
  }, [redirectUrl]);

  useEffect(() => {
    if (timeRemaining <= 0) return;
    const timer = setTimeout(() => setTimeRemaining(timeRemaining - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeRemaining]);

  useEffect(() => {
    setState((s) => ({ ...s, error: null }));
    setConfirmationResult(null);
    setTimeRemaining(0);
    // Don't reset pendingUser here - it's needed to link phone to email/google accounts
  }, [state.signupMethod]);

  const ensureRecaptcha = async () => {
    if (!recaptchaRef.current) {
      recaptchaRef.current = new FirebaseRecaptchaVerifier(auth, "recaptcha-container", { size: "invisible" });
      await recaptchaRef.current.render();
    }
    return recaptchaRef.current;
  };

  const handleCompleteLogin = async (user: User) => {
    const completeResult = await authService.completeLogin(user, redirectUrl);
    if (!completeResult.success || !completeResult.redirectUrl) {
      throw new Error(completeResult.error || "Session login failed");
    }
    window.location.href = completeResult.redirectUrl;
  };

  const handleEmailSignup = async (event: React.FormEvent) => {
    event.preventDefault();
    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      const nameValidation = validators.name(state.name);
      if (!nameValidation.valid) throw new Error(nameValidation.error);

      const emailValidation = validators.email(state.email);
      if (!emailValidation.valid) throw new Error(emailValidation.error);

      const passwordValidation = validators.password(state.password);
      if (!passwordValidation.valid) throw new Error(passwordValidation.error);

      const matchValidation = validators.passwordMatch(state.password, state.confirmPassword);
      if (!matchValidation.valid) throw new Error(matchValidation.error);

      if (!termsAccepted) throw new Error("Please accept the terms and conditions.");

      const createResult = await authService.createUserWithEmail(state.email, state.password);
      if (!createResult.success || !createResult.user) throw new Error(createResult.error || "Signup failed");

      const profileResult = await authService.updateProfile(createResult.user, { displayName: state.name });
      if (!profileResult.success) throw new Error(profileResult.error || "Profile update failed");

      // Clear sensitive data from state
      setState((s) => ({ ...s, password: "", confirmPassword: "" }));
      
      setPendingUser(createResult.user);
      setState((s) => ({ ...s, signupMethod: "email", loading: false }));
    } catch (err: any) {
      setState((s) => ({
        ...s,
        error: sanitizeUserVisibleMessage(err?.message, "Signup failed"),
        loading: false,
      }));
    }
  };

  const handleGoogleSignup = async () => {
    // Validate terms acceptance for Google signup (same as email/phone methods)
    if (!termsAccepted) {
      setState((s) => ({ ...s, error: "Please accept the terms and conditions." }));
      return;
    }

    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const googleResult = await authService.signInWithGoogle();
      if (!googleResult.success || !googleResult.user) throw new Error(googleResult.error || "Google sign up failed");

      if (googleResult.user.phoneNumber) {
        await handleCompleteLogin(googleResult.user);
        return;
      }

      setPendingUser(googleResult.user);
      setState((s) => ({ ...s, signupMethod: "google", loading: false }));
    } catch (err: any) {
      setState((s) => ({
        ...s,
        error: sanitizeUserVisibleMessage(err?.message, "Google sign up failed"),
        loading: false,
      }));
    }
  };

  const handlePhoneOTPSend = async () => {
    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      if (state.signupMethod === "phone") {
        const nameValidation = validators.name(state.name);
        if (!nameValidation.valid) throw new Error(nameValidation.error);
      }

      const phoneValidation = validators.phone(state.phone);
      if (!phoneValidation.valid) throw new Error(phoneValidation.error);

      if (!termsAccepted) throw new Error("Please accept the terms and conditions.");

      const formattedPhone = "+91" + state.phone.replace(/\D/g, "");
      const verifier = await ensureRecaptcha();
      const sendResult = await authService.sendPhoneVerificationCode(formattedPhone, verifier);
      if (!sendResult.success || !sendResult.confirmationResult) {
        throw new Error(sendResult.error || "Failed to send OTP");
      }

      setConfirmationResult(sendResult.confirmationResult);
      setTimeRemaining(120);
      setState((s) => ({ ...s, loading: false }));
    } catch (err: any) {
      setState((s) => ({
        ...s,
        error: sanitizeUserVisibleMessage(err?.message, "Failed to send OTP"),
        loading: false,
      }));
    }
  };

  const handlePhoneOTPVerify = async (code: string) => {
    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      if (!confirmationResult) throw new Error("OTP not sent");

      if (pendingUser) {
        const credentialResult = authService.createPhoneCredential(confirmationResult, code);
        if (!credentialResult.success || !credentialResult.credential) {
          throw new Error(credentialResult.error || "Failed to create phone credential");
        }

        const linkResult = await authService.linkPhoneCredential(pendingUser, credentialResult.credential);
        if (!linkResult.success || !linkResult.user) {
          throw new Error(linkResult.error || "Failed to link phone");
        }

        await handleCompleteLogin(linkResult.user);
        return;
      }

      const verifyResult = await authService.verifyPhoneOTP(confirmationResult, code);
      if (!verifyResult.success || !verifyResult.user) {
        throw new Error(verifyResult.error || "Verification failed");
      }

      if (state.name) {
        await authService.updateProfile(verifyResult.user, { displayName: state.name });
      }

      await handleCompleteLogin(verifyResult.user);
    } catch (err: any) {
      setState((s) => ({
        ...s,
        error: sanitizeUserVisibleMessage(err?.message, "Verification failed"),
        loading: false,
      }));
    }
  };

  if (state.authLoading) {
    return <Loading />;
  }

  const showPhoneVerification = state.signupMethod === "phone" || state.signupMethod === "email" || state.signupMethod === "google";
  const canShowOtp = !!confirmationResult;

  return (
    <CssVarsProvider theme={customTheme} disableTransitionOnChange>
      <CssBaseline />
      <GlobalStyles
        styles={{
          body: {
            fontFamily: "Roboto, sans-serif",
          },
        }}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "stretch",
          minHeight: "100vh",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            p: 4,
            overflowY: "auto",
          }}
        >
          <Header />

          <Box
            sx={{
              maxWidth: "400px",
              mx: "auto",
              width: "100%",
            }}
          >
            {state.signupMethod === "initial" ? (
              <>
                <Typography level="h2" component="h1" sx={{ mb: 1 }}>
                  Create your account
                </Typography>
                <Typography level="body-sm" sx={{ mb: 3 }}>
                  Sign up to continue
                </Typography>

                <form onSubmit={handleEmailSignup}>
                  <FormControl sx={{ mb: 1 }}>
                    <FormLabel>Full Name</FormLabel>
                    <Input
                      name="name"
                      placeholder="Your name"
                      value={state.name}
                      onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
                      disabled={state.loading}
                    />
                  </FormControl>

                  <FormControl sx={{ mb: 1 }}>
                    <FormLabel>Email</FormLabel>
                    <Input
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={state.email}
                      onChange={(e) => setState((s) => ({ ...s, email: e.target.value }))}
                      disabled={state.loading}
                    />
                  </FormControl>

                  <PasswordStrengthInput
                    password={state.password}
                    onChange={(value) => setState((s) => ({ ...s, password: value }))}
                  />

                  <FormControl sx={{ mt: 1 }}>
                    <FormLabel>Confirm Password</FormLabel>
                    <Input
                      name="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={state.confirmPassword}
                      onChange={(e) => setState((s) => ({ ...s, confirmPassword: e.target.value }))}
                      disabled={state.loading}
                    />
                  </FormControl>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mt: 2,
                    }}
                  >
                    <Checkbox
                      size="sm"
                      label={
                        <Typography level="body-sm">
                          I agree to the <Link href="/terms">terms</Link> and <Link href="/privacy">privacy policy</Link>
                        </Typography>
                      }
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                    />
                  </Box>

                  <ErrorDisplay
                    error={state.error}
                    onDismiss={() => setState((s) => ({ ...s, error: null }))}
                  />

                  <Button fullWidth sx={{ mt: 2 }} loading={state.loading} type="submit">
                    Create account
                  </Button>
                </form>

                <Divider sx={{ my: 3 }}>Or</Divider>

                <Button
                  fullWidth
                  variant="soft"
                  startDecorator={<GoogleIcon />}
                  onClick={handleGoogleSignup}
                  loading={state.loading}
                  disabled={state.loading}
                >
                  Continue with Google
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => setState((s) => ({ ...s, signupMethod: "phone" }))}
                  disabled={state.loading}
                >
                  Sign up with Phone
                </Button>

                <Box sx={{ textAlign: "center", mt: 3 }}>
                  <Typography level="body-sm">
                    Already have an account? <Link href="/signin">Sign in</Link>
                  </Typography>
                </Box>
              </>
            ) : showPhoneVerification ? (
              <>
                <Typography level="h3" sx={{ mb: 2 }}>
                  Verify Phone Number
                </Typography>
                <Typography level="body-sm" sx={{ mb: 3 }}>
                  Enter your phone number to verify your account
                </Typography>

                <ErrorDisplay
                  error={state.error}
                  onDismiss={() => setState((s) => ({ ...s, error: null }))}
                />

                {!canShowOtp ? (
                  <>
                    {state.signupMethod === "phone" && (
                      <FormControl sx={{ mb: 1 }}>
                        <FormLabel>Full Name</FormLabel>
                        <Input
                          name="name"
                          placeholder="Your name"
                          value={state.name}
                          onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
                          disabled={state.loading}
                        />
                      </FormControl>
                    )}

                    <PhoneNumberInput
                      value={state.phone}
                      onChange={(phone) => setState((s) => ({ ...s, phone }))}
                      disabled={state.loading}
                    />

                    <Button
                      fullWidth
                      sx={{ mt: 3 }}
                      loading={state.loading}
                      onClick={handlePhoneOTPSend}
                      disabled={state.phone.replace(/\D/g, "").length !== 10 || state.loading}
                    >
                      Send Code
                    </Button>
                  </>
                ) : (
                  <OTPVerification
                    phoneNumber={state.phone}
                    onVerify={handlePhoneOTPVerify}
                    onSendCode={handlePhoneOTPSend}
                    loading={state.loading}
                    otpSent={true}
                    timeRemaining={timeRemaining}
                  />
                )}

                <Button
                  fullWidth
                  variant="plain"
                  sx={{ mt: 3 }}
                  onClick={() => {
                    setState((s) => ({ ...s, signupMethod: "initial", phone: "" }));
                    setPendingUser(null);
                    setConfirmationResult(null);
                  }}
                >
                  Back
                </Button>
              </>
            ) : null}
          </Box>

          <Footer />
        </Box>

        <RightSide />
      </Box>

      <div id="recaptcha-container" style={{ display: "none" }} />
    </CssVarsProvider>
  );
}
